import { createRouter, createWebHistory } from 'vue-router'
import MainLayout from '@/components/templates/MainLayout.vue'
import { useAuthStore } from '@/stores/auth.store'
import { useFeaturesStore } from '@/stores/features.store'
import type { UserRole } from '@/types/auth.types'

// Convenience aliases
const ALL: UserRole[] = ['admin', 'people_culture', 'leader', 'employee']
const MGMT: UserRole[] = ['admin', 'people_culture', 'leader']
const HR: UserRole[] = ['admin', 'people_culture']
const ADMIN: UserRole[] = ['admin']

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // Auth routes (outside MainLayout)
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/auth/LoginView.vue'),
      meta: { public: true },
    },
    {
      path: '/auth/callback',
      name: 'auth-callback',
      component: () => import('@/views/auth/AuthCallbackView.vue'),
      meta: { public: true },
    },
    {
      path: '/',
      component: MainLayout,
      children: [
        // --- Personal section (all roles) ---
        {
          path: '',
          name: 'dashboard',
          component: () => import('@/views/Dashboard.vue'),
          meta: { roles: ALL },
        },
        {
          path: '/account',
          name: 'account',
          component: () => import('@/views/AccountView.vue'),
          meta: { roles: ALL },
        },
        {
          path: '/my-reviews',
          name: 'my-reviews',
          component: () => import('@/views/review/MyReviewsView.vue'),
          meta: { roles: ALL },
        },
        {
          path: '/faq',
          name: 'faq',
          component: () => import('@/views/FaqView.vue'),
          meta: { roles: ALL },
        },
        {
          path: '/discounts',
          name: 'discounts',
          component: () => import('@/views/discount/DiscountsView.vue'),
          meta: { roles: ALL },
        },
        {
          path: '/leave',
          name: 'leave',
          component: () => import('@/views/leave/LeaveView.vue'),
          meta: { roles: ALL, feature: 'leave.requests' },
        },
        {
          path: '/leave/admin',
          name: 'leave-admin',
          component: () => import('@/views/leave/LeaveAdminView.vue'),
          meta: { roles: HR, feature: 'leave.requests' },
        },
        {
          path: '/leave/admin/role-rates',
          name: 'leave-admin-role-rates',
          component: () => import('@/views/leave/LeaveRoleRatesView.vue'),
          meta: { roles: HR, feature: 'leave.requests' },
        },
        {
          path: '/leave/admin/role-rates/new',
          name: 'leave-admin-role-rate-new',
          component: () => import('@/views/leave/LeaveRoleRateNewView.vue'),
          meta: { roles: HR, feature: 'leave.requests' },
        },
        {
          path: '/leave/admin/role-rates/:id/edit',
          name: 'leave-admin-role-rate-edit',
          component: () => import('@/views/leave/LeaveRoleRateEditView.vue'),
          meta: { roles: HR, feature: 'leave.requests' },
        },
        {
          path: '/leave/admin/settings',
          name: 'leave-admin-settings',
          component: () => import('@/views/leave/LeaveSettingsView.vue'),
          meta: { roles: HR, feature: 'leave.requests' },
        },
        {
          path: '/leave/admin/adjustments',
          name: 'leave-admin-adjustments',
          component: () => import('@/views/leave/LeaveAdjustmentsView.vue'),
          meta: { roles: HR, feature: 'leave.requests' },
        },
        {
          path: '/leave/admin/adjustments/new',
          name: 'leave-admin-adjustment-new',
          component: () => import('@/views/leave/LeaveAdjustmentEditView.vue'),
          meta: { roles: HR, feature: 'leave.requests' },
        },
        {
          path: '/discounts/new',
          name: 'discount-new',
          component: () => import('@/views/discount/DiscountFormView.vue'),
          meta: { roles: HR },
        },
        {
          path: '/discounts/categories',
          name: 'discount-categories',
          component: () => import('@/views/discount/DiscountCategoriesView.vue'),
          meta: { roles: HR },
        },
        {
          path: '/discounts/:id/edit',
          name: 'discount-edit',
          component: () => import('@/views/discount/DiscountFormView.vue'),
          meta: { roles: HR },
        },
        {
          path: '/team/:auth_user_id',
          name: 'user-profile',
          component: () => import('@/views/team/UserProfileView.vue'),
          meta: { roles: MGMT },
        },
        {
          path: '/team',
          name: 'team',
          component: () => import('@/views/team/TeamView.vue'),
          meta: { roles: ALL },
        },

        {
          path: '/admin/features',
          name: 'features',
          component: () => import('@/views/admin/FeatureFlagsView.vue'),
          meta: { roles: ADMIN },
        },
        {
          path: '/admin/features/new',
          name: 'feature-new',
          component: () => import('@/views/admin/FeatureFlagNewView.vue'),
          meta: { roles: ADMIN },
        },

        // --- Management section ---
        {
          path: '/compliance',
          name: 'compliance',
          component: () => import('@/views/compliance/ComplianceView.vue'),
          meta: { roles: HR },
        },
        {
          path: '/compliance/:auth_user_id',
          name: 'compliance-detail',
          component: () => import('@/views/compliance/ComplianceDetailView.vue'),
          meta: { roles: HR },
        },
        {
          path: '/deadlines/manage',
          name: 'deadline-manage',
          component: () => import('@/views/deadline/DeadlineFormView.vue'),
          meta: { roles: HR },
        },
        {
          path: '/deadlines/new',
          name: 'deadline-new',
          component: () => import('@/views/deadline/DeadlineNewView.vue'),
          meta: { roles: HR },
        },
        {
          path: '/deadlines/:id/edit',
          name: 'deadline-edit',
          component: () => import('@/views/deadline/DeadlineNewView.vue'),
          meta: { roles: HR },
        },
        {
          path: '/deadlines',
          name: 'deadlines',
          component: () => import('@/views/deadline/DeadlinesView.vue'),
          meta: { roles: MGMT },
        },
        {
          path: '/reviews/new',
          name: 'review-new',
          component: () => import('@/views/review/ReviewFormView.vue'),
          meta: { roles: MGMT },
        },
        {
          path: '/reviews/:id/edit',
          name: 'review-edit',
          component: () => import('@/views/review/ReviewFormView.vue'),
          meta: { roles: MGMT },
        },
        {
          path: '/reviews',
          name: 'reviews',
          component: () => import('@/views/review/ReviewsView.vue'),
          meta: { roles: MGMT },
        },
        {
          path: '/whats-next/:auth_user_id/edit',
          name: 'whats-next-edit',
          component: () => import('@/views/whats-next/WhatsNextEditView.vue'),
          meta: { roles: MGMT },
        },
        {
          path: '/role-fit',
          name: 'role-fit',
          component: () => import('@/views/role-fit/RoleFitView.vue'),
          meta: { roles: MGMT },
        },
        {
          path: '/compensations',
          name: 'compensations',
          component: () => import('@/views/compensation/CompensationView.vue'),
          meta: { roles: MGMT },
        },
        {
          path: '/reports',
          name: 'reports',
          component: () => import('@/views/reports/ReportList.vue'),
          meta: { roles: MGMT },
        },
        {
          path: '/reports/personal-review',
          name: 'report-personal-review',
          component: () => import('@/views/reports/PersonalReview.vue'),
          meta: { roles: MGMT },
        },
        {
          path: '/reports/people-ral-by-leader',
          name: 'report-people-ral-by-leader',
          component: () => import('@/views/reports/PeopleRalByLeader.vue'),
          meta: { roles: MGMT },
        },

        // --- Admin/HR section ---
        {
          path: '/people/new',
          name: 'personal-detail-new',
          component: () => import('@/views/people/PersonalDetailFormView.vue'),
          meta: { roles: HR },
        },
        {
          path: '/people/:auth_user_id/edit',
          name: 'personal-detail-edit',
          component: () => import('@/views/people/PersonalDetailFormView.vue'),
          meta: { roles: HR },
        },
        {
          path: '/people',
          name: 'people',
          component: () => import('@/views/people/PeopleView.vue'),
          meta: { roles: [...MGMT] },
        },
        {
          path: '/contracts/new',
          name: 'contract-new',
          component: () => import('@/views/contract/ContractFormView.vue'),
          meta: { roles: HR },
        },
        {
          path: '/contracts/:id/edit',
          name: 'contract-edit',
          component: () => import('@/views/contract/ContractFormView.vue'),
          meta: { roles: HR },
        },
        {
          path: '/contracts',
          name: 'contracts',
          component: () => import('@/views/contract/ContractsView.vue'),
          meta: { roles: [...MGMT] },
        },
        {
          path: '/devices/:auth_user_id/edit',
          name: 'device-edit',
          component: () => import('@/views/device/DeviceFormView.vue'),
          meta: { roles: HR },
        },
        {
          path: '/devices',
          name: 'devices',
          component: () => import('@/views/device/DevicesView.vue'),
          meta: { roles: [...MGMT] },
        },
        {
          path: '/roles/new',
          name: 'role-new',
          component: () => import('@/views/role/RoleFormView.vue'),
          meta: { roles: HR },
        },
        {
          path: '/roles/:id/edit',
          name: 'role-edit',
          component: () => import('@/views/role/RoleFormView.vue'),
          meta: { roles: HR },
        },
        {
          path: '/roles',
          name: 'roles',
          component: () => import('@/views/role/RolesView.vue'),
          meta: { roles: HR },
        },
      ],
    },

    // Catch-all: redirect unknown paths (e.g. stale Glide bookmarks like /dl/...)
    {
      path: '/:pathMatch(.*)*',
      redirect: { name: 'dashboard' },
    },
  ],
})

// Navigation guard — redirect to login if not authenticated, check role access
let authInitialized = false

/** Called from main.ts after auth.init() completes */
export function markAuthInitialized() {
  authInitialized = true
}

router.beforeEach(async (to) => {
  if (to.meta.public) return true

  const authStore = useAuthStore()

  // On first navigation (page load), auth might not be initialized yet.
  // Wait for it — init() is called in main.ts before mount, but the
  // router may start resolving before mount completes.
  if (!authInitialized) {
    try {
      await authStore.init()
    } catch {
      // init failed — will redirect to login below
    }
    authInitialized = true
  }

  if (!authStore.isAuthenticated) {
    return { name: 'login' }
  }

  // Role-based access check
  const requiredRoles = to.meta.roles as UserRole[] | undefined
  if (requiredRoles && authStore.currentUser) {
    if (!requiredRoles.includes(authStore.currentUser.role)) {
      return { name: 'dashboard' }
    }
  }

  // Feature-flag gate (meta.feature): flags load once after auth; unknown
  // or unloaded flags are OFF — gated features ship dark by default.
  const requiredFeature = to.meta.feature as string | undefined
  if (requiredFeature) {
    const featuresStore = useFeaturesStore()
    if (!featuresStore.loaded) await featuresStore.load()
    if (!featuresStore.isEnabled(requiredFeature)) {
      return { name: 'dashboard' }
    }
  }

  return true
})

export default router
